import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { useHealthData } from '../utils/hooks';

const services = [
  {
    name: 'Auth Token',
    desc: 'Provides OAuth2 tokens for API access.',
    url: '/api/auth/token'
  },
  {
    name: 'Ask PDF',
    desc: 'Ask questions about PDF documents using AI models.',
    url: '/api/ask-pdf'
  },
  {
    name: 'Download PDFs',
    desc: 'Manually download and upload PDFs to GCS.',
    url: '/api/download-pdfs'
  },
  {
    name: 'PDF Status',
    desc: 'Check status of PDFs for a product.',
    url: '/api/pdf-status'
  },
  {
    name: 'List PDFs',
    desc: 'List all PDFs in the GCS bucket.',
    url: '/api/list-pdfs'
  },
  {
    name: 'Health Check',
    desc: 'Basic health check for the server.',
    url: '/health'
  }
];

function Dashboard() {
  const { healthData, loading, error } = useHealthData();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ok': return 'success';
      case 'fail': case 'error': return 'error';
      case 'degraded': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    return status || 'Unknown';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            SE Tools Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Monitor and manage all SE Tools services from a single dashboard. Click a service in the left navigation for more details.
          </Typography>
            
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Health Check Error</AlertTitle>
              {error}
            </Alert>
          )}
          
          {healthData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {new Date(healthData.timestamp).toLocaleString()} 
                {healthData.uptime && ` • Uptime: ${Math.floor(healthData.uptime / 3600)}h ${Math.floor((healthData.uptime % 3600) / 60)}m`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="services table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {healthData?.services && Object.entries(healthData.services).map(([serviceName, serviceData]) => {
                const displayName = serviceName.charAt(0).toUpperCase() + serviceName.slice(1).replace(/([A-Z])/g, ' $1');
                return (
                  <TableRow
                    key={serviceName}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {displayName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {serviceData.message || 'Service health check'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(serviceData.status)}
                        color={getStatusColor(serviceData.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {serviceData.bucket && `Bucket: ${serviceData.bucket}`}
                        {serviceData.data && `Items: ${Object.keys(serviceData.data).length}`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default Dashboard;
